import { useState } from "react";
import type { FormEvent } from "react";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import type { AdminListEntry, AdminRole } from "../lib/types";
import { formatDate, relativeTime } from "../lib/format";
import { Badge, Button, Card, ConfirmButton, ErrorState, Input, Modal, PageHeader, Select, Spinner, Table, Td } from "../components/ui";

const ROLE_TONE = { super_admin: "purple", admin: "blue", viewer: "slate" } as const;

interface Provisioned {
  registrationUrl: string;
  totpSecret: string;
  otpauthUri: string;
}

export default function Admins() {
  const { admin: me } = useAuth();
  const { data, loading, error, reload } = useApi<{ admins: AdminListEntry[] }>("/admins");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" as AdminRole });
  const [provisioned, setProvisioned] = useState<Provisioned | null>(null);
  const [regUrl, setRegUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.post<Provisioned>("/admins", form);
      setProvisioned(res);
      setShowCreate(false);
      setForm({ email: "", name: "", role: "admin" });
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorState message={error ?? "Failed to load"} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        title="Admin management"
        subtitle="Portal admins, their passkeys, and sessions — super admin only"
        actions={<Button onClick={() => setShowCreate(true)}>Add admin</Button>}
      />
      {actionError && <ErrorState message={actionError} />}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <Table headers={["Admin", "Role", "Passkeys", "Sessions", "Last login", "Actions"]}>
          {data.admins.map((a) => {
            const isMe = a.id === me?.id;
            const pendingRegistration = a.passkey_count === 0 && a.registration_token_expires_at;
            return (
              <tr key={a.id}>
                <Td>
                  <span className="font-medium">{a.email}</span>
                  {isMe && <Badge tone="green">you</Badge>}
                  <span className="block text-xs text-slate-500">{a.name}</span>
                  {pendingRegistration && (
                    <span className="block text-xs text-amber-600">
                      No passkey yet — registration link valid until {formatDate(a.registration_token_expires_at)}
                    </span>
                  )}
                </Td>
                <Td>
                  {isMe ? (
                    <Badge tone={ROLE_TONE[a.role]}>{a.role.replace("_", " ")}</Badge>
                  ) : (
                    <Select
                      value={a.role}
                      disabled={busy}
                      onChange={(e) => act(() => api.patch(`/admins/${a.id}`, { role: e.target.value }))}
                    >
                      <option value="viewer">viewer</option>
                      <option value="admin">admin</option>
                      <option value="super_admin">super admin</option>
                    </Select>
                  )}
                </Td>
                <Td>{a.passkey_count}</Td>
                <Td>{a.active_sessions}</Td>
                <Td className="text-xs" title={formatDate(a.last_login_at)}>
                  {a.last_login_at ? relativeTime(a.last_login_at) : "never"}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        act(async () => {
                          const res = await api.post<{ registrationUrl: string }>(`/admins/${a.id}/registration-token`);
                          setRegUrl(res.registrationUrl);
                        })
                      }
                    >
                      New passkey link
                    </Button>
                    {!isMe && (
                      <>
                        <ConfirmButton
                          label="Sign out"
                          confirmLabel="Revoke sessions?"
                          variant="secondary"
                          disabled={busy}
                          onConfirm={() => act(() => api.delete(`/admins/${a.id}/sessions`))}
                        />
                        <ConfirmButton
                          label="Remove"
                          confirmLabel="Really remove?"
                          disabled={busy}
                          onConfirm={() => act(() => api.delete(`/admins/${a.id}`))}
                        />
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      </div>

      {showCreate && (
        <Modal title="Add admin" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Email</label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Name</label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Role</label>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole })} className="w-full">
                <option value="viewer">viewer — read-only</option>
                <option value="admin">admin — read/write, no admin management</option>
                <option value="super_admin">super admin — full access</option>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Creating…" : "Create admin"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {provisioned && (
        <Modal title="Admin provisioned — save these now" onClose={() => setProvisioned(null)}>
          <div className="space-y-4 text-sm">
            <p className="text-amber-700">
              These credentials are shown once. Share them with the new admin over a secure channel.
            </p>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-600">Passkey registration link (7 days)</p>
              <code className="block break-all rounded bg-slate-50 p-2 text-xs">{provisioned.registrationUrl}</code>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-600">TOTP backup secret</p>
              <code className="block break-all rounded bg-slate-50 p-2 text-xs">{provisioned.totpSecret}</code>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-600">Authenticator app URI</p>
              <code className="block break-all rounded bg-slate-50 p-2 text-xs">{provisioned.otpauthUri}</code>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setProvisioned(null)}>Done — I've saved them</Button>
            </div>
          </div>
        </Modal>
      )}

      {regUrl && (
        <Modal title="New passkey registration link" onClose={() => setRegUrl(null)}>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              One-time link, valid 7 days. Any existing registration link for this admin is now void.
            </p>
            <code className="block break-all rounded bg-slate-50 p-2 text-xs">{regUrl}</code>
            <div className="flex justify-end">
              <Button onClick={() => setRegUrl(null)}>Done</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
